import slideToggle from '../utils/slideToggle.js';
import { fetchProducts } from '../utils/fetchProducts.js';

// Inline cart functionality
const inlineCart = () => {
  const cartHeaderIcon = document.getElementById('cart-icon-bubble');
  const inlineCart = document.querySelector('.inline-cart');
  const inlineCartClose = document.querySelector('.inline-cart_close');
  const inlineCartPageOverlay = document.querySelector('.inline-cart__page-overlay');

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.attributeName === 'class' && inlineCart.classList.contains('active')) {
        // Lock page scroll via overflow on <html> (the scroll container).
        // See html.body--no-scroll in _inline-cart.scss — position:fixed on
        // <body> is avoided because it destroys the scroll position.
        document.documentElement.classList.add('body--no-scroll');

        (async () => {
          await renderCartItems();
          getCartTotal();
        })();
      } else if (mutation.attributeName === 'class') {
        document.documentElement.classList.remove('body--no-scroll');
        // Other add-to-cart paths still add this class to <body>; it's inert
        // there (no CSS targets it) — clear it so it doesn't linger.
        document.body.classList.remove('body--no-scroll');
      }
    });
  });

  observer.observe(inlineCart, { attributes: true });

  function toggleCart() {
    inlineCart.classList.toggle('active');
    inlineCartPageOverlay.classList.toggle('active');
  }

  cartHeaderIcon.addEventListener('click', (event) => {
    // The icon is an <a href>; without this the default navigation appends a
    // hash to the URL and scrolls the page to the top.
    event.preventDefault();
    toggleCart();
  });
  inlineCartClose.addEventListener('click', toggleCart);
  inlineCartPageOverlay.addEventListener('click', toggleCart);

  // /cart bounces to the previous page with this flag set (layout/theme.liquid)
  // so the shopper sees the drawer, never the legacy cart page.
  try {
    if (sessionStorage.getItem('novomins:open-cart')) {
      sessionStorage.removeItem('novomins:open-cart');
      if (!inlineCart.classList.contains('active')) toggleCart();
    }
  } catch (e) {
    // Storage blocked: nothing to restore.
  }

  // Inline failure message at the top of the drawer. Cart calls used to fail
  // silently (console only), so a 422 / 429 looked like a dead button.
  const cartErrorEl = inlineCart.querySelector('.inline-cart__error');
  let cartErrorTimer;
  function showCartError(message) {
    if (!cartErrorEl) return;
    const generic = (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
    cartErrorEl.textContent = message || generic;
    cartErrorEl.hidden = false;
    clearTimeout(cartErrorTimer);
    cartErrorTimer = setTimeout(() => {
      cartErrorEl.hidden = true;
    }, 6000);
  }

  // Resolves with the parsed body; rejects with the shopper-facing description
  // on a non-2xx (Shopify sends { status, message, description } on 422).
  async function readCartResponse(res) {
    let body = null;
    try {
      body = await res.json();
    } catch (e) {
      body = null;
    }
    if (!res.ok) {
      throw new Error((body && (body.description || body.message)) || '');
    }
    return body;
  }

  const shipping = window.cartData.baseShippingPrice || 0;
  let freeShipping = false;
  const initialLoadCartItems = document.querySelectorAll(".inline-cart-item");

  const productIds = Array.from(initialLoadCartItems).map(item => {
    const productId = item.dataset.productId;
    return productId;
  });

  fetchProducts(productIds)
    .then(products => {
      const cartItems = document.querySelectorAll(".inline-cart-item");

      const productMap = new Map();
      products.forEach((product) => {
        const numericId = product.id;
        productMap.set(numericId, product);
      });

      let subTotalPrice = 0;

      cartItems.forEach(item => {
        const productId = item.dataset.productId;
        const productData = productMap.get(productId);
        if (!productData) return;

        const quantity = Number(item.querySelector(".displayed-quantity").innerText);
        const tagEl = item.querySelector('.cart-item__tag');
        const compareAtPriceEl = item.querySelector('.cart-item__compare-at-price');
        const savingPercentageEl = item.querySelector('.cart-item__savings--percentage');
        const pricePerProduct = item.dataset.pricePerProduct;

        if (tagEl && productData.supportingTagLine) {
          tagEl.textContent = productData.supportingTagLine;
        }
        if (compareAtPriceEl && productData.compareAtPrice) {
          compareAtPriceEl.textContent = formatMoney((productData.compareAtPrice * quantity) * 100);
          item.dataset.compareAtPrice = productData.compareAtPrice;
        }

        if (savingPercentageEl && pricePerProduct && productData.compareAtPrice) {
          savingPercentageEl.innerText = `${(((productData.compareAtPrice - (pricePerProduct / 100)) / productData.compareAtPrice) * 100).toFixed(0)}%`;
        }

        subTotalPrice += item.dataset.compareAtPrice * quantity;
      });
      // The totals rows are filled from /cart.js by updatePriceValues (via
      // getCartTotal below); the compare-at sum is no longer shown.
    })
    .catch(console.error);

  // Delivery-info accordion: markup removed 2026-10-22 (Figma inline cart);
  // kept null-safe in case the blocks are restored.
  const deliveryInfo = document.querySelector('.cart-delivery-info');
  if (deliveryInfo) {
    deliveryInfo.addEventListener('click', function () {
      slideToggle(document.querySelector('.cart-delivery-info__expanded'), 300);
    });
  }

  // "Add discount code" is a collapsed link; the input + Apply reveal on tap.
  const discountToggle = document.querySelector('[data-cart-discount-toggle]');
  const discountForm = document.querySelector('[data-cart-discount-form]');
  if (discountToggle && discountForm) {
    discountToggle.addEventListener('click', () => {
      const open = discountForm.hidden;
      discountForm.hidden = !open;
      discountToggle.setAttribute('aria-expanded', String(open));
      if (open) discountForm.querySelector('input')?.focus();
    });
  }

  function showDiscountError(codeValue) {
    const errorEl = document.querySelector('.discount-code-error');
    if (!errorEl) return;
    errorEl.textContent = `Invalid discount code - ${codeValue}`;
    slideToggle(errorEl, 300);
    setTimeout(() => {
      slideToggle(errorEl, 300);
    }, 5000);
  }

  document.querySelector('.apply-discount').addEventListener('click', async () => {
    const codeInput = document.getElementById('discount-code');
    const codeValue = codeInput.value.trim().toLowerCase();
    if (!codeValue) return;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: codeValue })
    })
      .then(readCartResponse)
      .then(res => {
        const discountCodes = res.discount_codes || [];
        const matchedCode = discountCodes.find(dc => dc.code.toLowerCase() === codeValue);

        // Not echoed back at all (unknown code) or echoed as not applicable:
        // both are "invalid" to the shopper. This used to throw on an
        // undeclared variable and show nothing.
        if (!matchedCode || !matchedCode.applicable) {
          showDiscountError(codeValue);
          return;
        }

        const coupon = createCouponElement(codeValue);
        document.querySelector('.display-discount').appendChild(coupon);
        getCartTotal();

        codeInput.value = '';
      })
      .catch(() => {
        showDiscountError(codeValue);
      });
  })

  document.querySelectorAll(".inline-cart-item").forEach(item => {
    bindActions(item);
  });

  // ── Cart suggestions ──────────────────────────────────────────────────────
  // Two blocks share the slot above the cart total:
  //   • Kids "Complete the Routine" — product data ships as a JSON blob in
  //     inline-cart.liquid (the section is on every page, so no HTML up front);
  //     the cards are built here the first time the block is shown.
  //   • Adult "Frequently bought with" — built here from bought-together.json,
  //     generated from real order history by scripts/build-bought-together.mjs.
  // An adult product anywhere in the cart wins. Kids-only carts get the kids
  // block. Only one is ever visible.

  const suggestionsConfig = window.cartSuggestions || {};
  const kidsProductIds = new Set((suggestionsConfig.kidsProductIds || []).map(String));
  const kidsProducts = Array.isArray(suggestionsConfig.kidsProducts) ? suggestionsConfig.kidsProducts : [];
  let boughtTogetherPromise = null;

  // Small static asset, fetched once on first cart render and reused after.
  function loadBoughtTogether() {
    if (!boughtTogetherPromise) {
      boughtTogetherPromise = fetch(suggestionsConfig.dataUrl)
        .then(res => (res.ok ? res.json() : {}))
        .catch(() => ({}));
    }
    return boughtTogetherPromise;
  }

  function getCartProductIds() {
    return Array.from(document.querySelectorAll('li.inline-cart-item'))
      .map(item => String(item.dataset.productId));
  }

  const isKidsProduct = id => kidsProductIds.has(String(id));

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, ch => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
    ));
  }

  // Both blocks use the same card markup, so the slider helpers work on either
  // one — pass the block root and they find their own track and dots.
  function updateDots(root) {
    const track = root?.querySelector('.cart-routine__track');
    const dotsWrap = root?.querySelector('.cart-routine__dots');
    if (!track || !dotsWrap) return;

    const visibleCards = Array.from(track.querySelectorAll('.cart-routine__card:not([hidden])'));
    dotsWrap.innerHTML = '';

    // Only advertise the grab cursor when there's actually somewhere to drag to.
    track.classList.toggle('is-draggable', track.scrollWidth > track.clientWidth);

    if (visibleCards.length < 2) return;

    visibleCards.forEach(card => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'cart-routine__dot';
      dot.setAttribute('tabindex', '-1');
      dot.addEventListener('click', () => {
        track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      });
      dotsWrap.appendChild(dot);
    });

    syncActiveDot(root);
  }

  function syncActiveDot(root) {
    const track = root?.querySelector('.cart-routine__track');
    const dotsWrap = root?.querySelector('.cart-routine__dots');
    if (!track || !dotsWrap || !dotsWrap.children.length) return;

    const visibleCards = Array.from(track.querySelectorAll('.cart-routine__card:not([hidden])'));
    const cardWidth = visibleCards[0]?.offsetWidth || 1;
    const index = Math.min(
      Math.round(track.scrollLeft / cardWidth),
      dotsWrap.children.length - 1
    );

    Array.from(dotsWrap.children).forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  }

  async function addSuggestionToCart(card) {
    const variantId = card.dataset.variantId;
    if (!variantId) return;
    const overlay = card.querySelector('.cart-routine__card-loading');
    if (overlay) overlay.style.display = 'flex';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) throw new Error('Add failed: ' + res.status);
      await renderCartItems();
      getCartTotal();
    } catch (err) {
      console.error('Suggestion add to cart failed:', err);
    } finally {
      if (overlay) overlay.style.display = 'none';
    }
  }

  // Click-and-drag to scroll the row on desktop. Touch pointers are ignored so
  // native momentum scrolling is left exactly as it is on mobile.
  const DRAG_THRESHOLD = 5; // px of travel before it counts as a drag, not a click

  function enableDragScroll(track) {
    let activePointer = null;
    let startX = 0;
    let startScrollLeft = 0;
    let travelled = 0;
    let dragged = false;

    track.addEventListener('pointerdown', event => {
      // A click is always preceded by a pointerdown, so clearing the flag here
      // means a drag that ended without a click can't suppress a later one.
      dragged = false;
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth) return;

      activePointer = event.pointerId;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
      travelled = 0;
      track.classList.add('is-dragging');
    });

    track.addEventListener('pointermove', event => {
      if (event.pointerId !== activePointer) return;
      const dx = event.clientX - startX;
      travelled = Math.max(travelled, Math.abs(dx));

      // Only capture once it's clearly a drag, so a plain click on ADD still
      // reaches the button.
      if (travelled > DRAG_THRESHOLD) {
        dragged = true;
        // Throws NotFoundError if the pointer is no longer active — that only
        // means we can't capture it, which isn't worth breaking the drag over.
        try {
          if (!track.hasPointerCapture(activePointer)) track.setPointerCapture(activePointer);
        } catch { /* keep dragging without capture */ }
      }
      track.scrollLeft = startScrollLeft - dx;
    });

    const endDrag = event => {
      if (event.pointerId !== activePointer) return;
      try {
        if (track.hasPointerCapture(activePointer)) track.releasePointerCapture(activePointer);
      } catch { /* already released */ }
      track.classList.remove('is-dragging');
      activePointer = null;
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Swallow the click that browsers fire at the end of a drag, so releasing
    // over a card doesn't add it to the cart.
    track.addEventListener('click', event => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    // Card images are draggable by default; without this the browser starts a
    // native image drag instead of scrolling the row.
    track.addEventListener('dragstart', event => event.preventDefault());
  }

  // Delegated so it covers cards added to the adult track after binding.
  function bindSuggestionBlock(root) {
    if (!root || root.dataset.bound) return;
    root.dataset.bound = '1';

    root.addEventListener('click', event => {
      const button = event.target.closest('[data-cart-routine-add]');
      if (!button) return;
      const card = button.closest('.cart-routine__card');
      if (card) addSuggestionToCart(card);
    });

    const track = root.querySelector('.cart-routine__track');
    if (track) {
      track.addEventListener('scroll', () => syncActiveDot(root), { passive: true });
      enableDragScroll(track);
    }
  }

  function buildSuggestionCard(product) {
    const card = document.createElement('div');
    card.className = 'cart-routine__card';
    card.setAttribute('data-cart-routine-card', '');
    card.dataset.productId = product.id;
    card.dataset.variantId = product.variantId || '';

    // Kids cards carry a Liquid-formatted price; adult cards a raw decimal.
    const price = product.priceFormatted
      ? escapeHtml(product.priceFormatted)
      : product.price != null
        ? window.formatMoney(Math.round(Number(product.price) * 100))
        : '';
    const ratingSku = product.ratingSku
      || `${product.handle};${product.sku || ''};${product.variantId || ''}`;
    // "Buy 3 for 2" on every card in the pooled range (kids cards already
    // carry the badge; adult cards are checked against the discount's own
    // target collection, published by the section).
    const threeForTwoIds = (window.cartSuggestions?.threeForTwoIds || []).map(String);
    const badge = product.badge
      || (threeForTwoIds.includes(String(product.id))
        ? ((window.cartLocaleTranslations || {}).routineBuyXForY || 'Buy 3 for 2')
        : '');

    card.innerHTML = `
      <div class="cart-routine__card-loading" aria-hidden="true">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="4" opacity="0.25" />
          <path d="M22 12a10 10 0 0 1-10 10" stroke="#447fbf" stroke-width="4" stroke-linecap="round">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
          </path>
        </svg>
      </div>
      ${product.imgUrl
        ? `<img class="cart-routine__img" src="${escapeHtml(product.imgUrl)}" alt="${escapeHtml(product.imgAltText)}" loading="lazy">`
        : ''}
      <div class="cart-routine__content">
        <div class="ruk_rating_snippet cart-routine__rating" data-sku="${escapeHtml(ratingSku)}"></div>
        <p class="cart-routine__card-title">${escapeHtml(product.title)}</p>
        <div class="cart-routine__card-price">
          <span class="cart-routine__price-stack">${badge
            ? `<span class="cart-routine__3for2">${escapeHtml(badge)}</span>`
            : ''}<strong>${price}</strong></span>
          <button type="button" class="cart-routine__add" data-cart-routine-add aria-label="Add ${escapeHtml(product.title)}">
            ADD +
          </button>
        </div>
      </div>
    `;
    return card;
  }

  // Reviews.io only scans the page on load, so freshly built cards need a nudge.
  function refreshRatingWidgets(root) {
    if (typeof ratingSnippet !== 'function' || typeof reviewsIoStore === 'undefined') return;
    try {
      ratingSnippet('ruk_rating_snippet', {
        store: reviewsIoStore,
        color: '#f3c621',
        textClr: '#333333',
        linebreak: false,
        usePolaris: true,
        text: 'Reviews'
      });
    } catch (err) {
      // A missing rating is cosmetic — never let it break the cart.
      console.warn('Reviews.io rating refresh skipped:', err);
    }
  }

  // Stale cards must go with the signature, or a later render can short-circuit
  // on a signature whose cards are no longer in the DOM.
  function clearSuggestions(root, track) {
    if (track) track.innerHTML = '';
    if (root) delete root.dataset.signature;
  }

  // Returns true when it managed to render at least one adult suggestion.
  async function renderAdultSuggestions(inCart) {
    const root = document.querySelector('[data-cart-bought]');
    const track = root?.querySelector('[data-cart-bought-track]');
    if (!track) return false;

    // Shopify returns cart lines newest-first, so the FIRST adult line is the
    // one the shopper most recently added. Verified against the live cart:
    // adding A then B then C yields [C, B, A]. Later lines are fallbacks for
    // when the newest product has no data of its own.
    const drivers = inCart.filter(id => !isKidsProduct(id));
    if (!drivers.length) {
      clearSuggestions(root, track);
      return false;
    }

    const data = await loadBoughtTogether();
    const driver = drivers.find(id => (data[id] || []).some(pid => !inCart.includes(String(pid))));
    if (!driver) {
      clearSuggestions(root, track);
      return false;
    }

    const suggestionIds = (data[driver] || [])
      .map(String)
      .filter(id => !inCart.includes(id));

    // Skip the work entirely if this is the same set we already rendered.
    const signature = `${driver}:${suggestionIds.join(',')}`;
    if (root.dataset.signature === signature && track.children.length) {
      updateDots(root);
      return true;
    }

    let products = [];
    try {
      products = await fetchProducts(suggestionIds);
    } catch (err) {
      console.error('Could not load cart suggestions:', err);
      clearSuggestions(root, track);
      return false;
    }

    // Keep the order-history ranking rather than whatever the API returned in.
    const byId = new Map(products.map(product => [String(product.id), product]));
    const ordered = suggestionIds
      .map(id => byId.get(id))
      .filter(product => product && product.variantId && product.availableForSale !== false);

    if (!ordered.length) {
      clearSuggestions(root, track);
      return false;
    }

    track.innerHTML = '';
    ordered.forEach(product => track.appendChild(buildSuggestionCard(product)));
    root.dataset.signature = signature;

    refreshRatingWidgets(root);
    updateDots(root);
    return true;
  }

  // Kids cards are built once, from the JSON blob, the first time the block is
  // shown; after that only the hidden state changes. Products already in the
  // cart are hidden.
  function renderKidsRoutine(root, inCart) {
    const track = root.querySelector('[data-cart-routine-track]');
    if (track && !track.children.length && kidsProducts.length) {
      kidsProducts.forEach(product => {
        if (!product || !product.variantId) return;
        track.appendChild(buildSuggestionCard({
          ...product,
          imgAltText: product.title,
          badge: (window.cartLocaleTranslations || {}).routineBuyXForY || 'Buy 3 for 2'
        }));
      });
      refreshRatingWidgets(root);
    }
    root.querySelectorAll('.cart-routine__card').forEach(card => {
      card.hidden = inCart.includes(String(card.dataset.productId));
    });
    updateDots(root);
  }

  async function updateCartSuggestions() {
    const kidsBlock = document.querySelector('[data-cart-routine]');
    const adultBlock = document.querySelector('[data-cart-bought]');
    const inCart = getCartProductIds();

    const showAdult = inCart.some(id => !isKidsProduct(id))
      ? await renderAdultSuggestions(inCart)
      : false;
    const showKids = !showAdult && inCart.some(isKidsProduct);

    if (kidsBlock) {
      kidsBlock.classList.toggle('is-visible', showKids);
      if (showKids) renderKidsRoutine(kidsBlock, inCart);
    }
    if (adultBlock) adultBlock.classList.toggle('is-visible', showAdult);

    // Whichever block is showing takes the points block's place.
    if (inlineCart) inlineCart.classList.toggle('has-cart-suggestions', showAdult || showKids);
  }

  function initCartSuggestions() {
    bindSuggestionBlock(document.querySelector('[data-cart-routine]'));
    bindSuggestionBlock(document.querySelector('[data-cart-bought]'));

    // Keep in sync whenever the cart item list re-renders.
    const list = document.querySelector('.inline-cart-items');
    if (list) new MutationObserver(() => updateCartSuggestions()).observe(list, { childList: true });

    updateCartSuggestions();
  }

  initCartSuggestions();

  function bindActions(item) {
    const removeButton = item.querySelector(".remove-item");
    const increaseQuantity = item.querySelector(".qty-plus");
    const decreaseQuantity = item.querySelector(".qty-minus");

    let isProcessing = false;

    const guardedAction = (fn) => {
      return async (...args) => {
        if (isProcessing) return;
        isProcessing = true;
        try {
          await fn(...args);
        } finally {
          isProcessing = false;
        }
      };
    };

    removeButton.addEventListener("click", guardedAction(() => updateQuantity(item, 0)));
    increaseQuantity.addEventListener("click", guardedAction(() => updateQuantity(item, 1)));
    decreaseQuantity.addEventListener("click", guardedAction(() => updateQuantity(item, -1)));

  }

  let isUpdating = new Set();

  async function refreshCartUI() {
    await renderCartItems();
    await getCartTotal();
  }

  // Dr Free Gift app integration: when the app adds/removes a gift (e.g. the
  // £45 cap) it calls window.refreshCart instead of reloading the page — this
  // is the app's supported hook for custom drawer carts. Exposing our own
  // re-render keeps the drawer open and shows the gift in place, no reload.
  window.refreshCart = refreshCartUI;

  function updateQuantity(item, value) {
    const itemKey = item.dataset.key;

    if (isUpdating.has(itemKey)) return;
    isUpdating.add(itemKey);

    const displayedQuantity = Number(item.querySelector(".displayed-quantity").innerText);
    let newQuantity = displayedQuantity + value;

    if (value === 0) newQuantity = 0;

    const overlay = item.querySelector('.inline-cart-item__loading-overlay');
    overlay.style.display = 'flex';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: newQuantity })
    })
      .then(readCartResponse)
      .then(async cart => {
        await checkPromoItems(cart);
        await refreshCartUI();
      })
      .catch(async (err) => {
        showCartError(err && err.message);
        await refreshCartUI();
      })
      .finally(() => {
        if (overlay && overlay.isConnected) {
          overlay.style.display = "none";
        }
        isUpdating.delete(itemKey);
      });
  }

  async function matchCartKeys(cart) {
    const newCartKeys = new Set(cart.items.map(i => i.key));
    const domItems = document.querySelectorAll('.inline-cart-item');

    // Find DOM items with keys that no longer exist in the cart
    const mismatchedItems = Array.from(domItems).filter(domItem => {
      return !newCartKeys.has(domItem.dataset.key);
    });

    if (mismatchedItems.length === 1) {
      const staleDomItem = mismatchedItems[0];

      // Find the item in cart that isn't in the DOM
      const domKeys = new Set(Array.from(domItems).map(i => i.dataset.key));
      const updatedCartItem = cart.items.find(item => !domKeys.has(item.key));

      if (updatedCartItem) {
        // Patch the DOM element with the new key
        staleDomItem.dataset.key = updatedCartItem.key;
        staleDomItem.querySelectorAll('[data-key]').forEach(el => {
          el.dataset.key = updatedCartItem.key;
        });
      }
    }

    return;
  }

  async function getCartTotal() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Failed to fetch cart data');
      const cart = await res.json();

      // The threshold now arrives per-country and already in the presentment
      // currency (snippets/free-shipping-config.liquid), so it is compared
      // directly — no FX conversion. It used to be one base-currency value for
      // every market, which promised free shipping at ~£30-worth of local
      // currency in markets whose real threshold is higher (IE/DE €40, FR/ES
      // €50) or which have no free-shipping rate at all (NL, IT, BE, CH, ...).
      const freeShippingThreshold = Number(window.cartData.freeShippingThreshold) || 0;
      const freeShippingOffered = window.cartData.freeShippingPossible === 'true' && freeShippingThreshold > 0;
      const totalPriceInCents = cart.total_price;
      const totalPriceFormatted = (totalPriceInCents / 100).toFixed(2);

      if (window.NovominsPDP && typeof window.NovominsPDP.setCartTotal === 'function') {
        window.NovominsPDP.setCartTotal(totalPriceInCents / 100);
      }

      if (cart.discount_codes.length > 0) {
        const discountContainer = document.querySelector('.display-discount');

        cart.discount_codes.forEach(code => {
          if (code.applicable === true) {
            const exists = discountContainer.querySelector(`[data-discount-code="${code.code}"]`);
            if (!exists) {
              const coupon = createCouponElement(code.code);
              coupon.dataset.discountCode = code.code;
              discountContainer.appendChild(coupon);
            }
          }
        });
      }

      // Free delivery unlocks on the cart total; where no free-shipping rate
      // exists the delivery row stays hidden rather than promising one.
      freeShipping = freeShippingOffered && totalPriceInCents / 100 >= freeShippingThreshold;

      updateProductPageGifting(totalPriceInCents);
      hideGiftProductControls();
      renderFreeGifts(cart);
      updatePriceValues(cart, freeShippingOffered);

      return totalPriceFormatted;
    } catch (err) {
      console.error('Error fetching cart total:', err);
      return null;
    }
  }

  function updateProductPageGifting(totalPriceInCents) {
    const giftingTiers = document.querySelectorAll('.gifting-tier');

    if (giftingTiers.length === 0) return;

    giftingTiers.forEach(tier => {
      const goal = Number(tier.dataset.goal) || 0;
      if ((totalPriceInCents / 100) >= goal) {
        tier.querySelector('.gifting-tier--unlocked').style.display = 'block';
        tier.querySelector('.gifting-tier--locked').style.display = 'none';
      } else {
        tier.querySelector('.gifting-tier--unlocked').style.display = 'none';
        tier.querySelector('.gifting-tier--locked').style.display = 'block';
      }
    });
  }


  function createCouponElement(code) {
    const div = document.createElement("div");
    div.className = "coupon";
    div.dataset.discountCode = code;

    div.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="currentColor">
        <path d="M17.78 3.09C17.45 2.443 16.778 2 16 2h-5.165c-.535 0-1.046.214-1.422.593l-6.82 6.89c0 .002 0 .003-.002.003-.245.253-.413.554-.5.874L.738 8.055c-.56-.953-.24-2.178.712-2.737L9.823.425C10.284.155 10.834.08 11.35.22l4.99 1.337c.755.203 1.293.814 1.44 1.533z" fill-opacity=".55"></path>
        <path d="M10.835 2H16c1.105 0 2 .895 2 2v5.172c0 .53-.21 1.04-.586 1.414l-6.818 6.818c-.777.778-2.036.782-2.82.01l-5.166-5.1c-.786-.775-.794-2.04-.02-2.828.002 0 .003 0 .003-.002l6.82-6.89C9.79 2.214 10.3 2 10.835 2zM13.5 8c.828 0 1.5-.672 1.5-1.5S14.328 5 13.5 5 12 5.672 12 6.5 12.672 8 13.5 8z"></path>
      </svg>
      ${code}
      <button class="coupon-remove" aria-label="Remove discount">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="6" r="6" fill="#d00"/>
          <path d="M4 4l4 4M8 4l-4 4" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    `;

    div.querySelector('.coupon-remove').addEventListener('click', () => onRemoveDiscount(code, div));

    return div;
  }

  async function onRemoveDiscount(codeToRemove, elementToRemove) {
    try {
      // Step 1: Get current cart
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      // Step 2: Get current discounts (may be null or [])
      const currentDiscounts = cart.discount_codes?.map(d => d.code) || [];

      // Step 3: Filter out the discount to remove
      const updatedDiscounts = currentDiscounts.filter(code => code !== codeToRemove);

      // Step 4: Update cart with remaining discounts
      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: updatedDiscounts.join(',') })
      });

      if (updateRes.ok && elementToRemove) {
        elementToRemove.remove();
      }

      await renderCartItems();
      getCartTotal();

    } catch (error) {
      console.error('Error removing discount:', error);
    }
  }

  async function animateAndRemove(item, duration = 300) {
    return new Promise(resolve => {
      item.style.transition = 'height .3s ease';
      item.style.overflow = 'hidden';
      item.style.height = `${item.scrollHeight}px`;
      item.offsetHeight; // force reflow
      item.style.height = '0px';

      setTimeout(() => {
        item.remove();
        requestAnimationFrame(() => {
          resolve(); // ensures browser has processed DOM change
        });
      }, duration);
    })
  }

  // Totals per the Figma (2026-10-22): Subtotal is the cart before any
  // discount, Delivery shows the base price struck through + FREE once the
  // threshold is met (or the base price while it isn't), Discount is what the
  // cart actually took off, Total is what the shopper pays. Compare-at (RRP)
  // based "savings" rows are gone.
  //
  // 2026-10-30 (agreed with Peter): while the delivery row shows a charge,
  // Total includes it, so the drawer's last number matches the first number
  // in checkout. sections/inline-cart.liquid applies the same rule on first
  // paint.
  function updatePriceValues(cart, freeShippingOffered) {
    const original = Number(cart.original_total_price) || 0;
    const total = Number(cart.total_price) || 0;
    const discount = Math.max(0, original - total);
    const hasItems = Array.isArray(cart.items) && cart.items.length > 0;
    const showDelivery = freeShippingOffered && hasItems;
    const deliveryCharge = showDelivery && !freeShipping ? Math.round(Number(shipping) * 100) || 0 : 0;

    const subtotalEl = document.querySelector('.cart-subtotal__amount');
    if (subtotalEl) subtotalEl.textContent = formatMoney(original);

    const totalEl = document.querySelector('.cart-total__amount');
    if (totalEl) totalEl.textContent = formatMoney(total + deliveryCharge);

    const discountRow = document.querySelector('.cart-discounts');
    const discountEl = document.querySelector('.cart-discounts__amount');
    if (discountRow && discountEl) {
      discountRow.hidden = discount <= 0;
      discountEl.textContent = `-${formatMoney(discount)}`;
    }

    const deliveryRow = document.querySelector('[data-cart-delivery]');
    if (deliveryRow) {
      deliveryRow.hidden = !showDelivery;
      deliveryRow.classList.toggle('is-free', freeShipping);
    }
  }

  // Free gifts (Dr Free Gift adds products whose handle starts with
  // docapp-free-gift-): listed in the footer with "FREE", never as line items.
  function isFreeGiftItem(item) {
    return typeof item.handle === 'string' && item.handle.indexOf('docapp-free-gift') === 0;
  }

  function renderFreeGifts(cart) {
    const wrap = document.querySelector('[data-cart-free-gifts]');
    const list = document.querySelector('[data-cart-free-gifts-list]');
    if (!wrap || !list) return;
    const gifts = (cart.items || []).filter(isFreeGiftItem);
    wrap.hidden = gifts.length === 0;
    const freeText = window.cartLocaleTranslations?.freeText || 'FREE';
    list.innerHTML = gifts.map((item) => {
      const isCap = /cap/i.test(item.handle || '');
      const capImg = window.cartLocaleTranslations?.giftCapImage || '';
      const img = isCap && capImg ? capImg : (item.featured_image?.url || item.image || '');
      const name = String(item.product_title || item.title || '').replace(/^Free\s+/i, '');
      return `
        <li class="cart-free-gifts__item" data-product-id="${item.product_id}">
          ${img ? `<img${isCap && capImg ? ' class="cart-free-gifts__cap"' : ''} src="${img}" alt="" width="48" height="48" loading="lazy">` : '<span class="cart-free-gifts__img-placeholder"></span>'}
          <span class="cart-free-gifts__name">${name.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))}</span>
          <span class="cart-free-gifts__price">${freeText}</span>
        </li>`;
    }).join('');
  }

  function updateHeaderIconNumber(count) {
    // Update cart count bubble in header
    const existingBubbleText = document.querySelector('.cart-count-bubble span');
    if (existingBubbleText) {
      existingBubbleText.textContent = count;
      return;
    }

    const cartIconContainer = document.querySelector('.header__icon--cart') || document.querySelector('#cart-icon-bubble');
    if (!cartIconContainer) return;

    const div = document.createElement('div');
    div.className = 'cart-count-bubble';

    div.innerHTML = `
      <span aria-hidden="true">${count}</span>
    `;

    cartIconContainer.appendChild(div);
  }

  async function checkPromoItems(cart) {
    const activeCart = cart;
    const promoProductIds = new Set([
      "15399219855740",
      "15399386317180",
      "15399391756668",
      "15399397753212"
    ]);

    // Filter out items that are not promo products
    const nonPromoItems = activeCart.items.filter(item => {
      return !promoProductIds.has(item.product_id.toString());
    });

    // If there's at least one non-promo item, do nothing
    if (nonPromoItems.length > 0) return activeCart;

    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    await renderCartItems();
    getCartTotal();

    return fetchCartData();
  }

  async function renderCartItems() {
    try {
      let cart = await fetchCartData();

      if (!cart.items?.length) {
        const remainingItems = document.querySelectorAll('.inline-cart-item');

        remainingItems.forEach(async item => {
          animateAndRemove(item);
        });

        updateHeaderIconNumber(cart.item_count);
        return;
      }

      const productIds = cart.items.map(item => item.product_id);
      let enrichedProducts = [];

      try {
        enrichedProducts = await fetchProducts(productIds);
      } catch (enrichmentError) {
        console.warn('Error enriching cart products, using cart payload fallback:', enrichmentError);
      }

      updateHeaderIconNumber(cart.item_count);

      const productMap = new Map();
      enrichedProducts.forEach(product => {
        productMap.set(product.id.toString(), product);
      });

      const cartList = document.querySelector('.inline-cart-items');
      cartList.innerHTML = '';

      cart.items.forEach(item => {
        if (isFreeGiftItem(item)) return; // shown in the Free gifts list instead
        const productData = productMap.get(item.product_id.toString()) || {};
        const itemImage = item.featured_image?.url || item.image || productData.imgUrl || '';
        const itemImageAlt = item.featured_image?.alt || item.product_title || productData.imgAltText || '';
        const compareAtPrice = Number(productData.compareAtPrice) || 0;
        const linePrice = typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
        const pricePerProduct = typeof item.final_price === 'number'
          ? item.final_price
          : (item.quantity > 0 ? linePrice / item.quantity : 0);
        const isPromoFreeGift = String(item.product_id) === '15993557680508'
          && Number(linePrice) === 0
          && Boolean(window.cartLocaleTranslations.freeGiftText);
        const itemPriceText = isPromoFreeGift
          ? window.cartLocaleTranslations.freeGiftText
          : formatMoney(linePrice);
        const saving = compareAtPrice
          ? (((compareAtPrice - (pricePerProduct / 100)) / compareAtPrice) * 100).toFixed(0)
          : null;
          const li = document.createElement('li');
          li.className = 'inline-cart-item';
          li.dataset.subscriptionProduct = item.selling_plan_allocation ? 'true' : 'false';
          li.dataset.productId = item.product_id;
          li.dataset.key = item.key;
          li.dataset.compareAtPrice = compareAtPrice || '';
          li.dataset.pricePerProduct = pricePerProduct;
          li.dataset.variantId = productData.variantId || item.variant_id;
          li.dataset.handle = item.handle || '';

          const blackFridayProductIds = [
            15692776407420, // Hair Health Bundle
            15692773753212, // Sleep & Recovery Bundle
            15692770705788, // Complete Man Bundle
            15692761465212, // Energy & Focus for Work Bundle
            15692758221180, // Gym Performance Bundle
            15692740690300, // Complete Women Bundle
            15692740133244, // Energy & Mood Bundle
            15692737905020, // Intimate Health Essentials
            15692737380732, // The Glow-Up Bundle
            15692736168316, // Menopause Support Stack
            15692734923132, // Hormone Harmony Bundle
            15692731842940, // Focus & Concentration Bundle
            15692730663292, // Growth & Development Bundle
            15692726206844, // The Ultimate Kids Immunity Stack
            15692720275836, // The Bedtime Basics
            15692715524476  // The Complete Sleep Solution
          ];

          let iBlackFridayProduct = blackFridayProductIds.indexOf(item.product_id);

          let deliverySchedule = item.selling_plan_allocation?.selling_plan?.options[1].value;

          const showCompareAt = (compareAtPrice * 100) > pricePerProduct;

          li.innerHTML = `
              <div class="inline-cart-item__loading-overlay">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="4" opacity="0.25" />
                  <path d="M22 12a10 10 0 0 1-10 10" stroke="#447fbf" stroke-width="4" stroke-linecap="round">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                  </path>
                </svg>
              </div>
              <img src="${itemImage}" alt="${itemImageAlt}">
      
              <div class="cart-item__content">
                <div class="cart-item__top">
                  <div class="cart-item__info">
                <p class="cart-item__title">${item.product_title}</p>
                <p class="cart-item__tag">${productData.supportingTagLine || ''}</p>
      
                <div class="cart-item__sub-details" style="${item.selling_plan_allocation ? 'display: block' : ''}">
                  <p class="cart-item__delivery">
                    ${(
              {
                '2-month': window.cartLocaleTranslations.delivery2,
                '3-month': window.cartLocaleTranslations.delivery3,
              }[deliverySchedule] ?? window.cartLocaleTranslations.delivery)
            }
                  </p>
                  ${saving ? `
                    <p class="cart-item__savings">${window.cartLocaleTranslations.itemSaving} <span class="cart-item__savings--percentage">${saving}%</span></p>
                  ` : ''}
                </div>

                ${iBlackFridayProduct !== -1 ? `
                  <div class="cart-item__sub-details" style="display: block;">
                    <p class="cart-item__savings">${window.cartLocaleTranslations.itemSaving} <span class="cart-item__savings--percentage">${saving}%</span></p>
                  </div>
                ` : ''}
              </div>
      
              <div class="cart-item__actions">
                <button class="remove-item" data-key="${item.key}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                    <path d="M1.5 4H3M3 4H15M3 4V14.5C3 14.8978 3.15804 15.2794 3.43934 15.5607C3.72064 15.842 4.10218 16 4.5 16H12C12.3978 16 12.7794 15.842 13.0607 15.5607C13.342 15.2794 13.5 14.8978 13.5 14.5V4H3ZM5.25 4V2.5C5.25 2.10218 5.40804 1.72064 5.68934 1.43934C5.97064 1.15804 6.35218 1 6.75 1H9.75C10.1478 1 10.5294 1.15804 10.8107 1.43934C11.092 1.72064 11.25 2.10218 11.25 2.5V4M6.75 7.75V12.25M9.75 7.75V12.25" stroke="#1D2025" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                
                <p class="cart-item__compare-at-price" style="display: ${showCompareAt ? "block" : "none"}">
                 ${formatMoney(compareAtPrice * item.quantity * 100)}
                </p>
                <p class="cart-item__price">${itemPriceText}</p>
      
                  </div>
                </div>

                <!-- Quantity sits in the gap under the copy, inside the column
                     beside the image. Mirrors inline-cart.liquid. -->
                <div class="cart-item__bottom">
                <div class="cart-item__qty">
                  <button class="qty-minus" data-key="${item.key}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="2" viewBox="0 0 9 2" fill="none">
                      <path d="M8.10156 1L0.601562 1" stroke="#1D2025"/>
                    </svg>
                  </button>
                  <span class="displayed-quantity">${item.quantity}</span>
                  <button class="qty-plus" data-key="${item.key}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <path d="M3.85156 0.25V7.75" stroke="#1D2025"/>
                      <path d="M7.60156 4L0.101562 4" stroke="#1D2025"/>
                    </svg>
                  </button>
                </div>
                </div>
              </div>
              <span class="cart-divider"></span>
            `;

          cartList.appendChild(li);
          bindActions(li);
          matchCartKeys(cart);
          // window.loadReviewsIoRatingSnippets();
        });
    } catch (err) {
      console.error('Error rendering cart:', err);
    }
  }

  function hideGiftProductControls() {
    // loop over cart items and hide qty controls and the remove button
    // for product with the data-product-id 15646588469628
    const giftProductId = "15646588469628";
    const cartItems = document.querySelectorAll('.inline-cart-item');

    cartItems.forEach(item => {
      const isGift = item.dataset.productId === giftProductId
        || (item.dataset.handle || '').indexOf('docapp-free-gift') === 0;
      if (isGift) {
        const removeButton = item.querySelector('.remove-item');
        const qtyControls = item.querySelector('.cart-item__qty');

        if (removeButton) removeButton.style.display = 'none';
        if (qtyControls) qtyControls.style.display = 'none';
      }
    });
  }

  // bindCartEvents();
  getCartTotal();

  function setViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  // Initial set
  setViewportHeight();

  // Update on resize or orientation change
  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', setViewportHeight);

  async function fetchCartData() {
    const response = await fetch('/cart.js');

    if (!response.ok) {
      throw new Error('Failed to load cart');
    }

    return response.json();
  }

  // Converts a base-currency amount into the shopper's presentment currency.
  // No longer used for the free-shipping threshold — that now arrives per
  // country already in presentment currency — but kept for any other
  // base-currency value that still needs converting.
  // eslint-disable-next-line no-unused-vars
  function toPresentmentCurrency(baseAmount) {
    const currencyRate = Number(window.Shopify?.currency?.rate) || 1;
    return baseAmount * currencyRate;
  }

};

export default inlineCart;